import type {
  ClientMediaItem,
  ClientOrganization
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ModalProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { AreaIcon, PersonIcon, PopulationIcon, WebIcon } from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { PreviewImageCarousel } from '@/features/profile-setting/components/organization-edit/PreviewImageCarousel';
import { PreviewModalShell } from '@/features/profile-setting/components/organization-edit/PreviewModalShell';
import type { TerritorialAuthorityEditFormValues } from '@/features/profile-setting/components/organization-edit/types';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { hasItems } from '@/shared/utils/array';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = Omit<ModalProps, 'children'> & {
  /**
   * The organization currently being edited. Used by the shell for the
   * header region name and by this modal for the map's home marker.
   */
  organization?: ClientOrganization;
  currentLogoImage?: ClientMediaItem;
};

// Format a raw population string ("12345") with locale grouping for display.
const formatPopulation = (raw: string, locale: string): string => {
  const trimmed = raw.trim();
  if (isEmptyOrNull(trimmed)) return '';
  const digits = trimmed.replaceAll(/[\s,.]/g, '');
  const asNumber = Number(digits);
  if (!Number.isFinite(asNumber)) return trimmed;
  return asNumber.toLocaleString(locale);
};

// Format a raw area string ("42.75") with locale-specific decimal separator.
const formatArea = (raw: string, locale: string): string => {
  const trimmed = raw.trim();
  if (isEmptyOrNull(trimmed)) return '';
  const normalized = trimmed.replace(',', '.');
  const asNumber = Number(normalized);
  if (!Number.isFinite(asNumber)) return trimmed;
  return asNumber.toLocaleString(locale, {
    maximumFractionDigits: 2
  });
};

// Strip the http(s) scheme from a URL for the compact preview display.
const displayUrl = (raw: string): string => {
  const trimmed = raw.trim();
  if (isEmptyOrNull(trimmed)) return '';
  return trimmed.replace(/^https?:\/\//i, '');
};

export const TerritorialAuthorityPreviewModal: FC<Props> = ({
  organization,
  currentLogoImage,
  ...modalProps
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t, i18n } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit.preview'
  });

  const { control } = useFormContext<TerritorialAuthorityEditFormValues>();
  const name = useWatch({ control, name: 'name' }) ?? '';
  const description = useWatch({ control, name: 'descriptionText' }) ?? '';
  const detailImages = useWatch({ control, name: 'detailImages' }) ?? [];
  const logoResult = useWatch({ control, name: 'logoResult' });
  const population = useWatch({ control, name: 'population' }) ?? '';
  const areaSquareKm = useWatch({ control, name: 'areaSquareKm' }) ?? '';
  const url = useWatch({ control, name: 'url' }) ?? '';

  const displayLogoMediaItem = logoResult?.localImage
    ? undefined
    : (currentLogoImage ?? undefined);
  const displayLogoLocalUri = logoResult?.localImage?.uri;

  const populationDisplay = formatPopulation(population, i18n.language);
  const areaDisplayRaw = formatArea(areaSquareKm, i18n.language);
  const areaDisplay = areaDisplayRaw
    ? t('formats.areaSquareKm', { value: areaDisplayRaw })
    : '';
  const urlDisplay = displayUrl(url);

  const iconProps = {
    fill: theme.colors.primary,
    width: 24,
    height: 24
  };

  return (
    <PreviewModalShell
      {...modalProps}
      organization={organization}
      scrollContentContainerStyle={styles.scrollExtras}
    >
      <View style={styles.orgHeader}>
        <ProfileAvatar
          viewSize={48}
          iconFallback={PersonIcon}
          mediaItem={displayLogoMediaItem}
          localImageUri={displayLogoLocalUri}
          mediaSize={MediaItemSize.SMALL}
        />
        <Text variant="titleLarge" numberOfLines={2} style={styles.orgName}>
          {name}
        </Text>
      </View>

      {!isEmptyOrNull(description) && (
        <Text variant="bodyLarge" style={styles.description}>
          {description}
        </Text>
      )}

      {hasItems(detailImages) && (
        <View style={styles.carouselWrapper}>
          <PreviewImageCarousel entries={detailImages} />
        </View>
      )}

      <ListSection style={styles.list}>
        {!isEmptyOrNull(populationDisplay) && (
          <ListItem
            icon={() => <PopulationIcon {...iconProps} />}
            title={t('rows.population')}
            badge={populationDisplay}
            hideRightArrow
          />
        )}
        {!isEmptyOrNull(areaDisplay) && (
          <ListItem
            icon={() => <AreaIcon {...iconProps} />}
            title={t('rows.areaSquareKm')}
            badge={areaDisplay}
            hideRightArrow
          />
        )}
        {!isEmptyOrNull(urlDisplay) && (
          <ListItem
            icon={() => <WebIcon {...iconProps} />}
            title={t('rows.url')}
            badge={urlDisplay}
            hideRightArrow
          />
        )}
      </ListSection>
    </PreviewModalShell>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    scrollExtras: {
      gap: 16
    },
    orgHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingHorizontal: 16,
      paddingTop: 8
    },
    orgName: {
      flex: 1,
      color: theme.colors.onSurface
    },
    description: {
      color: theme.colors.onSurface,
      paddingHorizontal: 16
    },
    carouselWrapper: { paddingHorizontal: 16 },
    list: {
      backgroundColor: 'transparent',
      paddingHorizontal: 16
    }
  });
